<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useOrgStore } from '@/stores/org'
import { useUserStore } from '@/stores/user'
import { useJoinLinksStore } from '@/stores/joinLinks'
import { useAccessStore } from '@/stores/access'
import { Link, Plus } from 'lucide-vue-next'

import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { toast } from '@/components/ui/toast'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import DialogQrCode from '@/components/dialogs/DialogQrCode.vue'
import { can } from '@/composables/useCan'
import JoinLinkCard from './JoinLinkCard.vue'
import DialogJoinLinkForm from './DialogJoinLinkForm.vue'

const projectStore = useProjectStore()

const orgStore = useOrgStore()

const userStore = useUserStore()

const joinLinksStore = useJoinLinksStore()

const accessStore = useAccessStore()
const route = useRoute()
const router = useRouter()

const projectId = computed(() => parseInt(route.params.project_id))
const orgId = computed(() => parseInt(route.params.org_id))
const project = computed(() => projectStore.current)
const members = computed(() => projectStore.members || [])
const currentMember = computed(() => userStore.member)

const canCreate = computed(() => can('project_invite', 'create', { projectId: projectId.value }))
const canRemoveMembers = computed(() => can('project_member', 'delete', { projectId: projectId.value }))

const byCreated = (a, b) => new Date(b.created_at) - new Date(a.created_at)

// Joined members get their project membership attached so the popover can offer removal.
const withMembership = (link) => ({
  ...link,
  joined: link.joined.map((m) => {
    const member = members.value.find((pm) => pm.member_id === m.member_id)
    return {
      ...m,
      project_member_id: member?.project_member_id || null,
      is_self: m.member_id === currentMember.value?.member_id,
    }
  }),
})

const links = computed(() => joinLinksStore.getLinks(projectId.value))
const active = computed(() => links.value.filter((l) => !l.is_revoked).sort(byCreated).map(withMembership))
const inactive = computed(() => links.value.filter((l) => l.is_revoked).sort(byCreated).map(withMembership))
const requestCount = computed(() => active.value.reduce((n, l) => n + (l.requests?.length || 0), 0))

const isLoading = ref(true)
const formOpen = ref(false)
const formLink = ref(null)
const qrOpen = ref(false)
const qrLink = ref(null)
const confirmDialog = ref(null)
const confirmState = ref({ title: '', description: '', confirmText: 'Confirm', variant: 'default', run: null })

onMounted(async () => {
  try {
    if (orgId.value) orgStore.setSelectedOrg(orgId.value)
    if (!project.value || project.value.project_id !== projectId.value) {
      await projectStore.fetchProject(projectId.value)
    }
    await Promise.all([
      projectStore.fetchMembers(projectId.value),
      joinLinksStore.fetchLinks(projectId.value),
      accessStore.ensure(orgId.value).catch(() => {}),
    ])
  } finally {
    isLoading.value = false
  }
})

const copy = async (link) => {
  try {
    await navigator.clipboard.writeText(link.url)
    toast({ title: 'Join link copied' })
  } catch (e) {
    toast({ title: "Couldn't copy the link", variant: 'destructive' })
  }
}

const showQr = (link) => {
  qrLink.value = link
  qrOpen.value = true
}

const print = (link) => {
  const { href } = router.resolve({
    name: 'ProjectJoinLinkPrint',
    params: { org_id: orgId.value, project_id: projectId.value, link_id: link.id },
  })
  window.open(href, '_blank')
}

const openCreate = () => {
  formLink.value = null
  formOpen.value = true
}

const openEdit = (link) => {
  formLink.value = link
  formOpen.value = true
}

const submitForm = async (fields) => {
  if (formLink.value) {
    await joinLinksStore.updateLink({ projectId: projectId.value, id: formLink.value.id, ...fields })
    toast({ title: 'Link updated' })
  } else {
    await joinLinksStore.createLink({ projectId: projectId.value, ...fields })
    toast({ title: 'Link created' })
  }
  formOpen.value = false
}

const ask = (state) => {
  confirmState.value = { variant: 'default', ...state }
  confirmDialog.value?.open()
}

const askRevoke = (link) =>
  ask({
    title: 'Revoke link',
    description: `“${link.name || 'This link'}” stops working immediately. Members who already joined keep their access.`,
    confirmText: 'Revoke',
    variant: 'error',
    run: async () => {
      await joinLinksStore.revokeLink({ projectId: projectId.value, id: link.id })
      toast({ title: 'Link revoked' })
    },
  })

const askDelete = (link) =>
  ask({
    title: 'Delete link',
    description: 'The revoked link is removed from this list.',
    confirmText: 'Delete',
    variant: 'error',
    run: () => joinLinksStore.deleteLink({ projectId: projectId.value, id: link.id }),
  })

const approve = async (link, member) => {
  await joinLinksStore.approveRequest({ projectId: projectId.value, id: link.id, memberId: member.member_id })
  toast({ title: `${member.display_name} joined the project` })
}

const askDecline = (link, member) =>
  ask({
    title: 'Decline request',
    description: `${member.display_name || 'This person'} will not join through this link.`,
    confirmText: 'Decline',
    variant: 'error',
    run: async () => {
      await joinLinksStore.declineRequest({ projectId: projectId.value, id: link.id, memberId: member.member_id })
      toast({ title: 'Request declined' })
    },
  })

const askRemoveMember = (link, member) =>
  ask({
    title: 'Delete member',
    description: `Are you sure you want to remove ${member.display_name || 'this member'} from the project?`,
    confirmText: 'Delete',
    variant: 'error',
    run: async () => {
      const deleted = await projectStore.deleteMember({
        projectId: projectId.value,
        memberId: member.project_member_id,
      })
      if (!deleted) return
      await joinLinksStore.removeJoined({ projectId: projectId.value, id: link.id, memberId: member.member_id })
      toast({ title: 'Member removed' })
    },
  })
</script>

<template>
  <div :key="projectId" data-testid="project-join-links-page">
    <div v-if="isLoading" class="flex h-64 items-center justify-center">
      <Spinner class="size-8" />
    </div>

    <div v-else class="space-y-6">
      <section>
        <div class="mb-2 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Active ({{ active.length }})</h3>
            <span
              v-if="requestCount"
              class="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
              data-testid="requests-count"
            >
              {{ requestCount }} {{ requestCount === 1 ? 'request' : 'requests' }}
            </span>
          </div>
          <Button
            v-if="canCreate"
            variant="ghost"
            size="sm"
            class="h-7 px-2 text-primary"
            data-testid="create-join-link"
            @click="openCreate"
          >
            <Plus class="mr-1 size-4" />
            New
          </Button>
        </div>
        <div v-if="active.length" class="space-y-3">
          <JoinLinkCard
            v-for="link in active"
            :key="link.id"
            :link="link"
            :removable="canRemoveMembers"
            :can-manage="canCreate"
            :manageable="canCreate"
            @approve="approve(link, $event)"
            @decline="askDecline(link, $event)"
            @copy="copy(link)"
            @qr="showQr(link)"
            @print="print(link)"
            @edit="openEdit(link)"
            @revoke="askRevoke(link)"
            @remove="askRemoveMember(link, $event)"
          />
        </div>
        <div v-else class="flex flex-col items-center rounded-xl border border-dashed px-3 py-8 text-center">
          <Link class="mb-2 size-6 text-muted-foreground" />
          <p class="text-sm font-medium">No join links yet</p>
          <p class="mt-1 text-sm text-muted-foreground">Create a link to let people join by URL or QR code.</p>
          <Button v-if="canCreate" class="mt-4" size="sm" @click="openCreate">
            <Plus class="mr-1 size-4" />
            New
          </Button>
        </div>
      </section>

      <section v-if="inactive.length">
        <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Inactive ({{ inactive.length }})</h3>
        <div class="space-y-3">
          <JoinLinkCard
            v-for="link in inactive"
            :key="link.id"
            :link="link"
            :removable="canRemoveMembers"
            :can-manage="canCreate"
            @delete="askDelete(link)"
            @remove="askRemoveMember(link, $event)"
          />
        </div>
      </section>
    </div>

    <DialogJoinLinkForm v-model:open="formOpen" :link="formLink" @submit="submitForm" />
    <DialogQrCode
      v-model:open="qrOpen"
      :url="qrLink?.url || ''"
      :title="qrLink?.name || 'Join link'"
      @copy="copy(qrLink)"
    />
    <DialogConfirm
      ref="confirmDialog"
      :title="confirmState.title"
      :description="confirmState.description"
      :confirm-text="confirmState.confirmText"
      :variant="confirmState.variant"
      @confirm="confirmState.run?.()"
    />
  </div>
</template>
